import {
  Heading,
  HStack,
  SimpleGrid,
  Stack,
  Text,
  VStack,
  Spinner,
  useBreakpointValue,
  Select,
  Box,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useState, useCallback } from "react";
import actions from "../actions";
import { CognitoUser } from "amazon-cognito-identity-js";
import { KYDQRCode } from "@common/interfaces/KYDOrganization";
import { QRCodeSVG } from "qrcode.react";
import { KYDEvent } from "@common/interfaces/KYDEvent";
import { useDialog } from "../Common/Dialog";

const QRCodes = ({ currentUser }: { currentUser: CognitoUser | null }) => {
  const [qrCodes, setQRCodes] = useState<KYDQRCode[] | null>([]);
  const [events, setEvents] = useState<KYDEvent[] | null>([]);

  const [loading, setLoading] = useState<any>({});

  const toast = useToast();
  const dialog = useDialog();

  const updateLoading = useCallback((key: string, value: boolean) => {
    setLoading((l: any) => {
      return { ...l, [key]: value };
    });
  }, []);

  const fetchQRCodes = useCallback(
    async (silent?: boolean) => {
      if (!silent) {
        updateLoading("init", true);
      }
      let qr_codes;
      try {
        const _ = await actions.ADMIN.fetchOrganization();
        if (_ && _.qr_codes) {
          const events = await actions.ADMIN.fetchEvents();
          setQRCodes(_.qr_codes);
          setEvents(
            events.filter(
              (kyd_event) => Date.now() < new Date(kyd_event.end_at).getTime()
            )
          );
          qr_codes = _.qr_codes;
        }
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("init", false);
      return qr_codes;
    },
    [updateLoading, dialog]
  );

  useEffect(() => {
    fetchQRCodes();
  }, [updateLoading, fetchQRCodes]);

  const onUpdateQRCode = useCallback(
    async (qrCodeId: string, eventId: string) => {
      updateLoading(qrCodeId, true);
      try {
        await actions.ADMIN.updateQRCode(qrCodeId, eventId);
        await fetchQRCodes(true);
        toast({
          title: "QR Code Updated",
          description: `QR code has been updated`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading(qrCodeId, false);
    },
    [fetchQRCodes, updateLoading, dialog, toast]
  );

  return (
    <Stack spacing={{ base: "8", lg: "6" }}>
      <Stack
        spacing="4"
        direction={{ base: "column", lg: "row" }}
        justify="space-between"
        align={{ base: "start", lg: "center" }}
      >
        <Stack spacing="1">
          <Heading
            size={useBreakpointValue({ base: "xs", lg: "sm" })}
            fontWeight="medium"
          >
            <HStack>
              <Text fontSize={"lg"} fontWeight="bold">
                QR Codes
              </Text>
            </HStack>
          </Heading>
        </Stack>
      </Stack>

      {loading["init"] && (
        <VStack>
          <Spinner />
          <Text>Loading QR codes...</Text>
        </VStack>
      )}
      {!loading["init"] && qrCodes && qrCodes.length === 0 && (
        <Text>No QR Codes. Contact us for new QR codes.</Text>
      )}
      {qrCodes && qrCodes.length > 0 && (
        <Stack spacing={{ base: "5", lg: "6" }}>
          <SimpleGrid minChildWidth="270px" spacing={4}>
            {qrCodes.map((code) => (
              <Box
                key={code.id}
                rounded={"md"}
                borderWidth={"2px"}
                borderColor={"gray.200"}
              >
                <VStack p={3}>
                  <Text w="100%" textAlign={"center"} fontSize={"xs"}>
                    <a
                      style={{ textDecoration: "underline" }}
                      href={code.url}
                      target="_blank"
                      rel={"noreferrer"}
                    >
                      {code.url.split("https://").pop()}
                    </a>
                  </Text>
                  <HStack>
                    <Box
                      rounded={"md"}
                      w="120px"
                      h="120px"
                      bg={`${code.color}.500`}
                    ></Box>
                    <QRCodeSVG
                      value={code.url}
                      size={120}
                      mode="L"
                      imageSettings={{
                        src: "https://content.kydlabs.com/system/crown.png",
                        x: undefined,
                        y: undefined,
                        height: 20,
                        width: 20,
                        excavate: true,
                      }}
                    />
                  </HStack>

                  <Select
                    isDisabled={loading[code.id]}
                    onChange={(e) => onUpdateQRCode(code.id, e.target.value)}
                    defaultValue={code.event_id || "disabled"}
                  >
                    <option value="disabled" disabled={true}>
                      Select Event
                    </option>
                    {events?.map((kydEvent) => (
                      <option key={kydEvent.id} value={kydEvent.id}>
                        {`${kydEvent.display_start_at
                          .split(" ")
                          .slice(1, 3)
                          .join(" ")} - ${kydEvent.name}`}
                      </option>
                    ))}
                  </Select>
                </VStack>
              </Box>
            ))}
          </SimpleGrid>
        </Stack>
      )}
    </Stack>
  );
};

export default QRCodes;
